import { useQuery } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";

export interface Booking {
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone: string;
}

// Read-only: refreshing an appointment must never overwrite queued field work.
export async function loadJobBooking(id: string, saved: Booking) {
  try {
    return { ...await api.jobs.booking(id), cached: false };
  } catch (error) {
    if (error instanceof ApiError && error.status > 0) throw error;
    return { ...saved, cached: true };
  }
}

export function JobBooking({ id, start, end }: { id: string; start: string | null; end: string | null }) {
  const booking = useQuery({
    queryKey: ["job-booking", id, start, end], retry: false, networkMode: "always",
    refetchOnMount: "always", refetchInterval: 60_000,
    queryFn: () => loadJobBooking(id, {
      scheduled_start: start, scheduled_end: end,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });
  const data = booking.error ? undefined : booking.data;
  const format = (value: string) => new Intl.DateTimeFormat("en-AU", {
    timeZone: data?.timezone, dateStyle: "medium", timeStyle: "short",
  }).format(new Date(value));
  return <>
    {booking.isPending && <span>Checking booking…</span>}
    {booking.error && <span role="alert">Cannot check this booking. {booking.error.message}</span>}
    {data && <>
      <div>{data.scheduled_start ? `${format(data.scheduled_start)}${data.scheduled_end ? ` – ${format(data.scheduled_end)}` : ""}` : "Not scheduled"}</div>
      <div className="tiny muted">{data.timezone}</div>
      {data.cached && <p className="notice notice-warn">Saved device booking; it may have changed. Times shown in your device timezone. Reconnect and refresh before travelling.</p>}
    </>}
    <button className="btn" type="button" disabled={booking.isFetching} onClick={() => void booking.refetch()}>Refresh booking</button>
  </>;
}
